import { CatalogueApiVersion } from "api/catalog/catalogApi";
import { useValueChainsAll } from "api/catalog/queries/useValueChains";
import { SelectOption } from "appkit-react";
import Select from "components/Select";
import sortBy from "lodash/sortBy";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

interface IValueChainSelectProps {
  tenant?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  hasError?: boolean;
  errMsg?: any;
  placeholder?: string;
  apiVersion?: CatalogueApiVersion;
  bothApiVersions?: boolean;
}

const ValueChainSelect = ({
  tenant,
  value,
  onValueChange,
  apiVersion,
  bothApiVersions,
  ...selectProps
}: IValueChainSelectProps) => {
  const tenants = useSelector((state) => (state as any).global.data.tenants);
  const { data: valueChainsAll } = useValueChainsAll(undefined, 1);
  const { data: valueChainsAllV2 } = useValueChainsAll(undefined, 2);

  const valueChains = useMemo(() => {
    if (!tenant) return;

    const v1Content = (
      tenant
        ? valueChainsAll?.[tenant]
        : Object.keys(valueChainsAll || {})
            .map((k) => valueChainsAll?.[k])
            .flat()
    )?.filter(Boolean);
    const v2Content = (
      tenant
        ? valueChainsAllV2?.[tenant]
        : Object.keys(valueChainsAllV2 || {})
            .map((k) => valueChainsAllV2?.[k])
            .flat()
    )?.filter(Boolean);

    if (bothApiVersions) return sortBy([...(v1Content || []), ...(v2Content || [])], (item) => item?.order);
    if (apiVersion === 2) return sortBy(v2Content || [], (item) => item?.order);
    return sortBy(v1Content || [], (item) => item?.order);
  }, [apiVersion, bothApiVersions, valueChainsAll, valueChainsAllV2, tenant]);

  const handleSelect = useCallback(
    (values: string[]) => {
      if (typeof values === "string") {
        onValueChange(values);
        return;
      }
      const isAdd = values?.length > value?.length;
      if (isAdd) {
        const [addedValue] = values.filter((item) => !value.includes(item));
        const valuesToCommit = values.filter((item) => {
          if (valueChains?.map((valuechain) => valuechain?.id).includes(item) && item !== addedValue) {
            return false;
          }
          return true;
        });
        onValueChange(valuesToCommit);
      } else {
        onValueChange(values);
      }
    },
    [onValueChange, value, valueChains]
  );

  return (
    <Select
      multiple
      placeholder="Select a Value chain"
      showSearchOnToggle
      value={value}
      onSelect={handleSelect}
      {...selectProps}
    >
      {valueChains?.map(
        (valueChain) =>
          !!valueChain?.id && (
            <SelectOption value={valueChain.id} key={valueChain.id}>
              {valueChain.name}
              {tenant === undefined &&
                ` (${tenants.find((t: any) => t.domain.toUpperCase() === valueChain.domain)?.name})`}
            </SelectOption>
          )
      )}
    </Select>
  );
};

export default ValueChainSelect;
